import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Mail, Phone, MessageCircle, Search, Filter, Loader2, Trash2, Check, X,
  Copy, MapPin, Building2, Users, Inbox, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'pending',        label: 'New',           color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'contacted',      label: 'Contacted',     color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'converted',      label: 'Converted',     color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'not-interested', label: 'Not interested', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'spam',           label: 'Spam',          color: 'bg-red-100 text-red-700 border-red-200' }
];

const SOURCE_LABEL = {
  'homepage-hero': 'Homepage hero',
  'upgrade-to-smart-property': 'Upgrade card',
  null: 'Homepage hero',
  undefined: 'Homepage hero'
};

// Pre-built WhatsApp message templates
const WA_TEMPLATES = [
  {
    id: 'intro',
    label: 'Intro message',
    body: (name) =>
      `Hi${name ? ' ' + name : ''}! 👋 This is Manager from RentKollect. Thanks for requesting a demo — I'd love to show you how you can save 10+ hours a week managing your property. When is a good time for a quick 15-min call?`
  },
  {
    id: 'followup',
    label: 'Gentle follow-up',
    body: (name) =>
      `Hi${name ? ' ' + name : ''}, just circling back on your RentKollect demo request. Let me know a time that works for you — we can do English or Hindi, whichever you prefer. 😊`
  },
  {
    id: 'resend_link',
    label: 'Resend link',
    body: () =>
      `Here's the link again: https://rentkollect.com — let me know if you'd like me to walk you through it live.`
  }
];

const AdminDemoRequests = () => {
  const token = localStorage.getItem('admin_token');
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      const res = await axios.get(`${API}/admin/demo-requests`, { headers: authHeaders, params });
      setItems(res.data.items || []);
      setStats(res.data.stats || { total: 0, by_status: {}, by_source: {} });
    } catch (e) {
      toast({ title: 'Could not load demo requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, statusFilter, sourceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateItem = async (id, patch) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/demo-requests/${id}`, patch, { headers: authHeaders });
      toast({ title: 'Updated' });
      await fetchData();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/demo-requests/${id}`, { headers: authHeaders });
      toast({ title: 'Deleted' });
      await fetchData();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const openWhatsApp = (mobile, message) => {
    const phone = String(mobile).replace(/\D/g, '');
    const num = phone.length === 10 ? `91${phone}` : phone;
    const url = `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener');
  };

  const callNumber = (mobile) => {
    window.location.href = `tel:+91${String(mobile).replace(/\D/g, '')}`;
  };

  const copyMobile = (mobile) => {
    navigator.clipboard.writeText(`+91${String(mobile).replace(/\D/g, '')}`);
    toast({ title: 'Copied', description: `+91${mobile}` });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      String(it.mobile || '').includes(q) ||
      String(it.name || '').toLowerCase().includes(q) ||
      String(it.city || '').toLowerCase().includes(q) ||
      String(it.property_type || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const getStatus = (s) => STATUSES.find((x) => x.value === s) || STATUSES[0];

  return (
    <div className="space-y-5" data-testid="admin-demo-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Inbox className="text-amber-500" /> Demo Requests
          </h1>
          <p className="text-gray-600 mt-1">Every lead that came through the public site — track, follow up, convert.</p>
        </div>
        <Button
          data-testid="demo-refresh-btn"
          onClick={fetchData}
          variant="outline"
          disabled={loading}
          className="gap-2"
        >
          <Sparkles size={14} /> Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KpiCard label="Total leads" value={stats.total} color="from-slate-700 to-slate-900" />
        <KpiCard label="New" value={stats.by_status?.pending || 0} color="from-blue-500 to-blue-700" />
        <KpiCard label="Contacted" value={stats.by_status?.contacted || 0} color="from-amber-500 to-amber-700" />
        <KpiCard label="Converted" value={stats.by_status?.converted || 0} color="from-green-500 to-green-700" />
        <KpiCard label="From hero" value={stats.by_source?.['homepage-hero'] || 0} color="from-indigo-500 to-indigo-700" />
        <KpiCard label="From upgrade" value={stats.by_source?.['upgrade-to-smart-property'] || 0} color="from-purple-500 to-pink-600" />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="demo-search"
              type="text"
              placeholder="Search by name, mobile, city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <SelectFilter
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: '', label: 'All statuses' }, ...STATUSES]}
            testId="demo-filter-status"
          />
          <SelectFilter
            label="Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={[
              { value: '', label: 'All sources' },
              { value: 'homepage-hero', label: 'Homepage hero' },
              { value: 'upgrade-to-smart-property', label: 'Upgrade card' }
            ]}
            testId="demo-filter-source"
          />
        </div>
      </Card>

      {/* List */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-amber-500" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Inbox size={48} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900">No demo requests yet</h3>
            <p className="text-gray-500 text-sm mt-1">
              When people submit the demo form on <code className="bg-gray-100 px-1.5 rounded">rentkollect.com</code>, they'll show up here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((item) => {
              const status = getStatus(item.status || 'pending');
              const isExpanded = expandedId === item.id;
              const createdDate = item.created_at ? new Date(item.created_at) : null;
              return (
                <div
                  key={item.id}
                  data-testid={`demo-row-${item.id}`}
                  className="p-4 hover:bg-amber-50/30 transition-colors"
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {(item.name || '?').slice(0, 1).toUpperCase()}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{item.name || 'No name provided'}</p>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${status.color}`}>
                          {status.label}
                        </span>
                        <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {SOURCE_LABEL[item.source] || item.source || 'Homepage hero'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 flex-wrap">
                        <span className="flex items-center gap-1 font-mono">
                          <Phone size={12} /> +91 {item.mobile}
                        </span>
                        {item.email && (
                          <a
                            href={`mailto:${item.email}`}
                            className="flex items-center gap-1 text-gray-600 hover:text-emerald-600"
                            data-testid={`demo-email-${item.id}`}
                          >
                            ✉️ {item.email}
                          </a>
                        )}
                        {item.city && (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} /> {item.city}
                          </span>
                        )}
                        {item.property_type && (
                          <span className="flex items-center gap-1">
                            <Building2 size={12} /> {item.property_type}
                          </span>
                        )}
                        {item.beds && (
                          <span className="flex items-center gap-1">
                            <Users size={12} /> {item.beds} beds
                          </span>
                        )}
                        {(item.location_city || item.location_country) && (
                          <span className="flex items-center gap-1 text-[11px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                            📍 {[item.location_city, item.location_region, item.location_country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {item.device_type && (
                          <span className="flex items-center gap-1 text-[11px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded capitalize">
                            {item.device_type === 'mobile' ? '📱' : item.device_type === 'tablet' ? '📱' : '💻'} {item.device_type}
                          </span>
                        )}
                        {createdDate && (
                          <span className="text-gray-400 text-xs">
                            {createdDate.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <IconBtn testId={`demo-call-${item.id}`} title="Call" onClick={() => callNumber(item.mobile)} color="blue">
                        <Phone size={14} />
                      </IconBtn>
                      <IconBtn testId={`demo-copy-${item.id}`} title="Copy number" onClick={() => copyMobile(item.mobile)} color="gray">
                        <Copy size={14} />
                      </IconBtn>
                      <Button
                        data-testid={`demo-wa-${item.id}`}
                        size="sm"
                        onClick={() => openWhatsApp(item.mobile, WA_TEMPLATES[0].body(item.name))}
                        className="bg-green-500 hover:bg-green-600 text-white gap-1.5"
                      >
                        <MessageCircle size={14} /> WhatsApp
                      </Button>
                      <Button
                        data-testid={`demo-expand-${item.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      >
                        {isExpanded ? 'Less' : 'More'}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded — templates + status + note */}
                  {isExpanded && (
                    <div className="mt-4 ml-13 pl-13 space-y-4 bg-slate-50 rounded-lg p-4" data-testid={`demo-expanded-${item.id}`}>
                      {/* Capture context */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Capture context</p>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-gray-700 bg-white rounded-md border border-gray-200 p-3">
                          {item.page_url && (
                            <div className="flex gap-1.5"><dt className="text-gray-500">Page:</dt><dd className="font-mono truncate">{item.page_url}</dd></div>
                          )}
                          {item.referrer && (
                            <div className="flex gap-1.5"><dt className="text-gray-500">Referrer:</dt><dd className="font-mono truncate">{item.referrer}</dd></div>
                          )}
                          {item.utm_source && (
                            <div className="flex gap-1.5"><dt className="text-gray-500">UTM:</dt><dd className="font-mono">{[item.utm_source, item.utm_medium, item.utm_campaign].filter(Boolean).join(' / ')}</dd></div>
                          )}
                          {item.ip && (
                            <div className="flex gap-1.5"><dt className="text-gray-500">IP:</dt><dd className="font-mono">{item.ip}</dd></div>
                          )}
                          {(item.device_os || item.device_browser) && (
                            <div className="flex gap-1.5"><dt className="text-gray-500">Device:</dt><dd>{[item.device_type, item.device_os, item.device_browser].filter(Boolean).join(' · ')}</dd></div>
                          )}
                          {(item.location_city || item.location_country) && (
                            <div className="flex gap-1.5"><dt className="text-gray-500">Location:</dt><dd>{[item.location_city, item.location_region, item.location_country].filter(Boolean).join(', ')}</dd></div>
                          )}
                          {item.user_agent && (
                            <div className="flex gap-1.5 col-span-full"><dt className="text-gray-500">UA:</dt><dd className="font-mono text-[10px] truncate text-gray-500">{item.user_agent}</dd></div>
                          )}
                        </dl>
                      </div>

                      {/* WhatsApp templates */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                          One-click WhatsApp templates
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {WA_TEMPLATES.map((tpl) => (
                            <Button
                              key={tpl.id}
                              data-testid={`demo-wa-tpl-${tpl.id}-${item.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => openWhatsApp(item.mobile, tpl.body(item.name))}
                              className="gap-1.5 bg-white"
                            >
                              <MessageCircle size={12} className="text-green-500" />
                              {tpl.label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Status setter */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Update status</p>
                        <div className="flex gap-2 flex-wrap">
                          {STATUSES.map((s) => (
                            <button
                              key={s.value}
                              data-testid={`demo-set-${s.value}-${item.id}`}
                              onClick={() => updateItem(item.id, { status: s.value })}
                              disabled={busyId === item.id || item.status === s.value}
                              className={`text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
                                item.status === s.value
                                  ? s.color + ' ring-2 ring-offset-1 ring-amber-300'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                              } disabled:opacity-60`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Note */}
                      <NoteEditor
                        initial={item.note || ''}
                        onSave={(note) => updateItem(item.id, { note })}
                        disabled={busyId === item.id}
                        testId={`demo-note-${item.id}`}
                      />

                      {/* Delete */}
                      <div className="flex justify-end">
                        <Button
                          data-testid={`demo-delete-${item.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => deleteItem(item.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 size={12} className="mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

// --- helpers ---

const KpiCard = ({ label, value, color }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl p-3 text-white shadow`}>
    <p className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{label}</p>
    <p className="text-2xl font-bold mt-0.5">{value}</p>
  </div>
);

const IconBtn = ({ children, onClick, title, testId, color = 'gray' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200',
    gray: 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200'
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      title={title}
      className={`p-2 rounded-lg border ${colors[color]} transition-colors`}
    >
      {children}
    </button>
  );
};

const SelectFilter = ({ label, value, onChange, options, testId }) => (
  <div className="flex items-center gap-1.5">
    <Filter size={12} className="text-gray-400" />
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const NoteEditor = ({ initial, onSave, disabled, testId }) => {
  const [text, setText] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setText(initial); setDirty(false); }, [initial]);
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Private note</p>
      <div className="flex gap-2">
        <textarea
          data-testid={testId}
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
          placeholder="Mentioned they have 45 beds in Koramangala, want to meet next week…"
          rows={2}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
        />
        {dirty && (
          <div className="flex flex-col gap-1">
            <Button
              data-testid={`${testId}-save`}
              size="sm"
              onClick={() => onSave(text)}
              disabled={disabled}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Check size={12} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setText(initial); setDirty(false); }}
              disabled={disabled}
            >
              <X size={12} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDemoRequests;
